/**
 * Centralized Discount Award Type Constants
 *
 * Canonical `systemKeyword` values returned by the OrderEntry API on
 * `IAoeCart.earnedDiscounts[]` (see PoeDiscountTypes / DiscountPlanAwardGateways
 * in Backoffice OrderEntry.BusinessLogic).
 *
 * NEVER compare against display labels - always use these constants.
 */

export const DiscountAwardType = {
  FREE_DOLLARS: 'FreeDollars',
  FREE_DOLLARS_OFF_ORDER: 'FreeDollarsOffOrder',
  PERCENTAGE_OFF_ITEM: 'PercentageOffItem',
  PERCENTAGE_OFF_ORDER: 'PercentageOffOrder',
  DISCOUNTED_SKU_FROM_LIST: 'DiscountedSkuFromList',
  DISCOUNTED_ITEM: 'DiscountedItem',
  SELECT_FREE_PRODUCT: 'SelectFreeProduct',
  FREE_PRODUCT: 'FreeProduct',
  FREE_SHIPPING: 'FreeShipping',
  COUPON: 'Coupon',
} as const

export type DiscountAwardTypeType =
  (typeof DiscountAwardType)[keyof typeof DiscountAwardType]

/**
 * How an award is summarised in the Host rewards summary.
 * - Currency: earned / remaining shown as money (host dollars)
 * - Count:    earned / remaining shown as a number of products
 * - Flag:     shown as simply "earned" (nothing left to redeem)
 */
export const HostRewardSummaryKind = {
  CURRENCY: 'Currency',
  COUNT: 'Count',
  FLAG: 'Flag',
} as const

export type HostRewardSummaryKindType =
  (typeof HostRewardSummaryKind)[keyof typeof HostRewardSummaryKind]

export function getDiscountAwardTypeLabelKey(systemKeyword: string): string {
  const map: Record<string, string> = {
    [DiscountAwardType.FREE_DOLLARS]: 'hostRewardsSummary.FreeDollars',
    [DiscountAwardType.FREE_DOLLARS_OFF_ORDER]: 'hostRewardsSummary.FreeDollars',
    [DiscountAwardType.PERCENTAGE_OFF_ITEM]: 'hostRewardsSummary.PercentageOffItem',
    [DiscountAwardType.PERCENTAGE_OFF_ORDER]: 'hostRewardsSummary.PercentageOffOrder',
    [DiscountAwardType.DISCOUNTED_SKU_FROM_LIST]: 'hostRewardsSummary.DiscountedSkuFromList',
    [DiscountAwardType.DISCOUNTED_ITEM]: 'hostRewardsSummary.DiscountedItem',
    [DiscountAwardType.SELECT_FREE_PRODUCT]: 'hostRewardsSummary.SelectFreeProduct',
    [DiscountAwardType.FREE_PRODUCT]: 'hostRewardsSummary.FreeProduct',
    [DiscountAwardType.FREE_SHIPPING]: 'hostRewardsSummary.FreeShipping',
    [DiscountAwardType.COUPON]: 'hostRewardsSummary.Coupon',
  }
  return map[systemKeyword] ?? 'hostRewardsSummary.Other'
}

export function getHostRewardSummaryKind(
  systemKeyword: string,
): HostRewardSummaryKindType {
  switch (systemKeyword) {
    case DiscountAwardType.FREE_DOLLARS:
      return HostRewardSummaryKind.CURRENCY
    case DiscountAwardType.PERCENTAGE_OFF_ITEM:
    case DiscountAwardType.DISCOUNTED_SKU_FROM_LIST:
    case DiscountAwardType.DISCOUNTED_ITEM:
    case DiscountAwardType.SELECT_FREE_PRODUCT:
      return HostRewardSummaryKind.COUNT
    default:
      return HostRewardSummaryKind.FLAG
  }
}
